import { GroupingState, SortingState } from "@tanstack/react-table";
import { useMemo } from "react";
import useEvent from "react-use-event-hook";
import {
  ArrayParam,
  ObjectParam,
  StringParam,
  useQueryParam,
} from "use-query-params";

import {
  CARD_FILTERS_QS_KEY,
  deserializeQsFilterModel,
  deserializeQsNamedFilterModel,
  deserializeSortingStateQs,
  hasKeys,
  IFilterSchemaObject,
  Log,
  serializeQsFilterModel,
  serializeSortingStateQs,
} from "@ryde/shared";

export const useCustomQueryParams = () => {
  const [filtersQs, setFiltersQs] = useQueryParam("filters", ObjectParam);
  const [tableSortingQs, setTableSortingQs] = useQueryParam("sort", ArrayParam);
  const [tableGroupingQs, setTableGroupingQs] = useQueryParam(
    "group",
    ArrayParam,
  );
  const [tableSearchQs, setTableSearchQs] = useQueryParam(
    "search",
    StringParam,
  );
  const [cardFiltersQs, setCardFiltersQs] = useQueryParam(
    CARD_FILTERS_QS_KEY,
    ObjectParam,
  );

  const setFilterSearchQsByValue = useEvent(
    (filterSchema: IFilterSchemaObject): void => {
      const serializedFilterModel = serializeQsFilterModel(filterSchema ?? {});
      Log.debug(
        "Push current filters schema to qs:",
        filterSchema,
        serializedFilterModel,
      );

      if (hasKeys(serializedFilterModel)) {
        setFiltersQs?.(serializedFilterModel);
      } else {
        setFiltersQs?.(undefined);
      }
    },
  );
  const resetFilterSearchQs = useEvent(() => {
    setFiltersQs?.(undefined);
  });

  const filtersSchemaFromQs = useMemo(() => {
    if (!filtersQs || !hasKeys(filtersQs)) {
      return undefined;
    }

    return deserializeQsFilterModel(filtersQs);
  }, [filtersQs]);

  const setTableSortingQsByValue = useEvent(
    (tableSorting?: SortingState): void => {
      const serializedSortingModel = serializeSortingStateQs(
        tableSorting ?? [],
      );
      Log.debug(
        "Push current table sorting to qs:",
        tableSorting,
        serializedSortingModel,
      );

      if (hasKeys(serializedSortingModel)) {
        setTableSortingQs(serializedSortingModel);
      } else {
        setTableSortingQs(undefined);
      }
    },
  );

  const resetTableSortingQs = useEvent(() => {
    setTableSortingQs(undefined);
  });
  const tableSortingFromQS = useMemo(() => {
    if (!tableSortingQs || !hasKeys(tableSortingQs)) {
      return undefined;
    }

    return deserializeSortingStateQs(tableSortingQs);
  }, [tableSortingQs]);

  const setTableGroupingQsByValue = useEvent(
    (tableGrouping?: GroupingState): void => {
      Log.debug("Push current table grouping to qs:", tableGrouping);

      if (tableGrouping?.length) {
        setTableGroupingQs(tableGrouping ?? []);
      } else {
        setTableGroupingQs(undefined);
      }
    },
  );
  const resetTableGroupingQs = useEvent(() => {
    setTableGroupingQs(undefined);
  });

  const tableGroupingFromQS = useMemo(
    () => tableGroupingQs?.filter(value => value !== null),
    [tableGroupingQs],
  );

  const cardFiltersFromQs = useMemo(() => {
    if (!cardFiltersQs || !hasKeys(cardFiltersQs)) {
      return undefined;
    }

    return deserializeQsNamedFilterModel(cardFiltersQs);
  }, [cardFiltersQs]);

  const resetCardFiltersQs = useEvent(() => {
    setCardFiltersQs?.(undefined, "replaceIn");
  });

  const setTableSearchQsByValue = useEvent((search?: string): void => {
    setTableSearchQs(search || undefined);
  });

  const resetTableSearchQs = useEvent(() => {
    setTableSearchQs(undefined);
  });

  const resetAllQs = useEvent(() => {
    resetFilterSearchQs();
    resetTableGroupingQs();
    resetTableSortingQs();
    resetTableSearchQs();
  });

  return {
    setFilterSearchQsByValue,
    filtersSchemaFromQs,
    setTableSortingQsByValue,
    tableSortingFromQS,
    setTableGroupingQsByValue,
    tableGroupingFromQS,
    tableSearchFromQs: tableSearchQs ?? undefined,
    setTableSearchQsByValue,
    cardFiltersFromQs,
    resetCardFiltersQs,
    resetAllQs,
  };
};
